import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SurveyCrossTabItem } from "@/types/survey";

const IDENTITY_COLORS: Record<string, string> = {
  hobby: "#2563eb",
  aspiring: "#7c3aed",
  professional: "#16a34a",
  none: "#64748b",
};

const FORMAT_ORDER = ["novel", "webnovel", "fanfiction", "screenplay", "other"];
const IDENTITY_ORDER = ["hobby", "aspiring", "professional", "none"];

interface SurveyCrossTabChartProps {
  title: string;
  data: SurveyCrossTabItem[];
}

interface PivotedRow {
  writingFormat: string;
  total: number;
  [identity: string]: string | number;
}

function pivotData(
  data: SurveyCrossTabItem[],
  identities: string[],
): PivotedRow[] {
  const formats = new Set<string>(FORMAT_ORDER);
  data.forEach((d) => formats.add(d.writingFormat));

  const orderedFormats = [
    ...FORMAT_ORDER.filter((f) => formats.has(f)),
    ...Array.from(formats).filter((f) => !FORMAT_ORDER.includes(f)),
  ];

  return orderedFormats.map((format) => {
    const row: PivotedRow = { writingFormat: format, total: 0 };
    identities.forEach((id) => {
      row[id] = 0;
    });
    data
      .filter((d) => d.writingFormat === format)
      .forEach((d) => {
        row[d.writerIdentity] = ((row[d.writerIdentity] as number) ?? 0) + d.count;
        row.total += d.count;
      });
    return row;
  });
}

export function SurveyCrossTabChart({ title, data }: SurveyCrossTabChartProps) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
            데이터 없음
          </div>
        </CardContent>
      </Card>
    );
  }

  const identitiesInData = Array.from(
    new Set(data.map((d) => d.writerIdentity)),
  );
  const orderedIdentities = [
    ...IDENTITY_ORDER.filter((id) => identitiesInData.includes(id)),
    ...identitiesInData.filter((id) => !IDENTITY_ORDER.includes(id)),
  ];

  const pivoted = pivotData(data, orderedIdentities);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={pivoted} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="writingFormat"
                tick={{ fontSize: 12 }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {orderedIdentities.map((identity) => (
                <Bar
                  key={identity}
                  dataKey={identity}
                  stackId="identity"
                  fill={IDENTITY_COLORS[identity] ?? "#94a3b8"}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
